'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Briefcase,
  Building2,
  ChevronDown,
  Headphones,
  Mail,
  MessageSquare,
  Phone,
  SendHorizontal,
  Smile,
  Sparkles,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useMessages, useSendMessage } from '@kit/chat/hooks';
import type { BalleePageContext } from '@kit/chat/lib';
import {
  escalateSupportAction,
  sendSupportMessageAction,
} from '@kit/chat/server';
import type { DeliveryChannel } from '@kit/chat/types';
import { Button } from '@kit/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@kit/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@kit/ui/popover';
import { toast } from '@kit/ui/sonner';
import { Textarea } from '@kit/ui/textarea';
import { Trans } from '@kit/ui/trans';

import { useIsBalleeSupportResponder } from '~/lib/hooks/use-is-ballee-support-responder';
import { useIsSuperAdmin } from '~/lib/hooks/use-is-super-admin';
import {
  BookingInquiryForm,
  formatBookingInquiryBody,
  type BookingInquirySerialized,
} from './booking-inquiry-form';

/** Resolves the send button icon without nested ternaries */
function SendButtonIcon({
  isBalleeResponding,
  isSendingSupport,
  isAdminSupportResponse,
}: {
  isBalleeResponding: boolean;
  isSendingSupport: boolean;
  isAdminSupportResponse: boolean;
}) {
  if (isBalleeResponding) return <Sparkles className="h-5 w-5 animate-pulse" />;
  if (isSendingSupport) return <Headphones className="h-5 w-5 animate-pulse" />;
  if (isAdminSupportResponse) return <Headphones className="h-5 w-5" />;
  // SendHorizontal sits optically centered in a circular icon button;
  // the tilted Send paper plane reads high-right.
  return <SendHorizontal className="h-5 w-5" />;
}

/** Resolves the helper text below the composer without nested ternaries */
function ComposerHelperText({
  isSendingSupport,
  isAdminSupportResponse,
  isBalleeResponding,
}: {
  isSendingSupport: boolean;
  isAdminSupportResponse: boolean;
  isBalleeResponding: boolean;
}) {
  if (isSendingSupport) return <Trans i18nKey="chat:composer.sending" />;
  if (isAdminSupportResponse)
    return <Trans i18nKey="chat:composer.replyingToSupport" />;
  if (isBalleeResponding) return <Trans i18nKey="chat:composer.thinking" />;
  return <Trans i18nKey="chat:composer.pressEnter" />;
}

interface MessageComposerProps {
  conversationId: string;
  replyTo?: {
    id: string;
    content: string;
    senderName: string;
  } | null;
  onClearReply?: () => void;
  /** Whether this is a conversation with Flow AI */
  isBalleeConversation?: boolean;
  /** Whether this is a group conversation */
  isGroupConversation?: boolean;
  /** Whether this is a support conversation */
  isSupportConversation?: boolean;
  /**
   * BAL-580: set when the viewer answers this thread for an org. The reply
   * goes out as the org (the database stamps it), so say so before sending.
   */
  replyAsOrganizationName?: string | null;
  /**
   * Support case status ('ai' | 'open' | 'investigating' | 'resolved'), or
   * null for non-support conversations. While 'ai', the Flow AI auto-responds
   * to the user as the first responder; the user can escalate to a human.
   */
  supportStatus?: string | null;
  /** Page context for Flow AI responses */
  pageContext?: BalleePageContext | null;
  /** Recipient phone number (enables WhatsApp channel) */
  recipientPhone?: string | null;
  /** Recipient email address (enables Email channel) */
  recipientEmail?: string | null;
  /**
   * Optional intent flag (from ?intent= query param). Currently only
   * `booking` is honoured. Phase 2 of the booking-template WIP will
   * auto-open a structured booking inquiry sheet when this is set
   * and the conversation has no prior messages. Phase 1 wires the
   * prop through without behaviour change.
   * See: docs/wip/active/WIP_wiring_inbox_intent_booking_template_2026_05_13.md
   */
  initialIntent?: string;
  /**
   * Optional opener to pre-fill the composer with on mount. Used by the
   * marketplace "Message seller" deep-link so the buyer's first message
   * starts as an "is this still available?" prompt (WIP R1 stretch).
   */
  initialMessage?: string;
  /**
   * Whether the briefcase booking-inquiry trigger is offered on direct
   * conversations. Marketplace seller DMs pass false — the structured
   * booking form is a dancer-booking instrument, not a listing one.
   */
  showBookingInquiry?: boolean;
}

/** Channel option configuration */
const CHANNEL_OPTIONS: {
  value: DeliveryChannel;
  labelKey: string;
  icon: typeof MessageSquare;
  color: string;
}[] = [
  {
    value: 'in_app',
    labelKey: 'chat:composer.channel.inApp',
    icon: MessageSquare,
    color: '',
  },
  {
    value: 'whatsapp',
    labelKey: 'chat:composer.channel.whatsapp',
    icon: Phone,
    color: 'text-green-600',
  },
  {
    value: 'email',
    labelKey: 'chat:composer.channel.email',
    icon: Mail,
    color: 'text-blue-600',
  },
];

// Common emoji reactions
const QUICK_EMOJIS = ['👍', '❤️', '😊', '😂', '🎉', '👏', '🔥', '💯'];

/** Pattern to detect @Flow mentions in group messages */
const FLOW_MENTION_PATTERN = /@flow\b/i;

export function MessageComposer({
  conversationId,
  replyTo,
  onClearReply,
  isBalleeConversation = false,
  isGroupConversation = false,
  isSupportConversation = false,
  replyAsOrganizationName = null,
  supportStatus = null,
  pageContext,
  recipientPhone,
  recipientEmail,
  initialIntent,
  initialMessage,
  showBookingInquiry = true,
}: MessageComposerProps) {
  const { t } = useTranslation();
  const [content, setContent] = useState('');
  const [isBalleeResponding, setIsBalleeResponding] = useState(false);
  const [isSendingSupport, setIsSendingSupport] = useState(false);
  const [escalatedLocally, setEscalatedLocally] = useState(false);
  const [isEscalating, setIsEscalating] = useState(false);
  const [selectedChannel, setSelectedChannel] =
    useState<DeliveryChannel>('in_app');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { data: isSuperAdminFlag } = useIsSuperAdmin();
  const { data: isBalleeResponder } = useIsBalleeSupportResponder();
  // A support RESPONDER is a super admin OR a Ballee org member (masked
  // "Ballee"). Drives the responder composer, the channel selector, and the
  // "Talk to a human" escalate affordance (support→Ballee-org, Phase 4b).
  const isSupportResponder =
    Boolean(isSuperAdminFlag) || Boolean(isBalleeResponder);

  // Booking inquiry: surface a structured form when the inbox was
  // entered via the premium-dancer rail CTA (?intent=booking). The
  // sheet auto-opens once on mount IF the conversation has no prior
  // messages — keeping the structured flow committed without trapping
  // users already mid-thread. The inbox layout strips ?intent= from
  // the URL after the conversation loads, so this stays stable
  // across navigation. Users can also re-open it manually via the
  // briefcase button in the composer row.
  const isBookingIntent = initialIntent === 'booking';
  const showBookingButton =
    showBookingInquiry &&
    !isBalleeConversation &&
    !isSupportConversation &&
    !isGroupConversation;
  const [isBookingSheetOpen, setIsBookingSheetOpen] = useState(false);
  const bookingAutoOpenedRef = useRef(false);
  const listingPrefillAppliedRef = useRef(false);

  // Probe message count for the freshness gate. `useMessages` is
  // already memoised by conversationId in TanStack Query, so this
  // call shares cache with `MessageList`'s subscription — no extra
  // network round-trip.
  const { messages: priorMessages, isLoading: isLoadingPriorMessages } =
    useMessages({ conversationId });
  const hasPriorMessages = priorMessages.length > 0;

  useEffect(() => {
    if (!isBookingIntent) return;
    if (bookingAutoOpenedRef.current) return;
    if (!showBookingButton) return;
    // Wait for the message probe to resolve before deciding whether
    // to auto-open. Once we know there are prior messages we skip
    // the auto-open for the rest of the session.
    if (isLoadingPriorMessages) return;
    if (hasPriorMessages) {
      bookingAutoOpenedRef.current = true;
      return;
    }
    bookingAutoOpenedRef.current = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: deferred auto-open after the async freshness probe resolves; can't be moved to render because it depends on isLoadingPriorMessages settling
    setIsBookingSheetOpen(true);
  }, [
    isBookingIntent,
    showBookingButton,
    isLoadingPriorMessages,
    hasPriorMessages,
  ]);

  // Reset per-conversation state when switching conversations. Includes
  // the booking auto-open ref + sheet open state so a second
  // `?intent=booking` deep-link to a different dancer re-fires the
  // structured prompt instead of silently degrading to the briefcase
  // button. (Premium-dancer CTAs are the entire reason this feature
  // exists, so the re-arm path is load-bearing.)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resets local channel selector + booking state on conversation switch; intentional sync from prop
    setSelectedChannel('in_app');
    bookingAutoOpenedRef.current = false;

    setIsBookingSheetOpen(false);
    setEscalatedLocally(false);
  }, [conversationId]);

  // Listing prefill (marketplace "Message seller" deep-link). Seed the
  // composer with the "is this still available?" opener ONCE, and only for
  // a FRESH conversation — never clobber an existing thread the buyer
  // already had with this seller. Deferred to an effect (not the useState
  // initializer) so it can wait for the message-count probe, mirroring the
  // booking auto-open gate above.
  useEffect(() => {
    if (!initialMessage) return;
    if (listingPrefillAppliedRef.current) return;
    if (isLoadingPriorMessages) return;
    listingPrefillAppliedRef.current = true;
    if (hasPriorMessages) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot prefill after the async message-count probe resolves; only seeds when the composer is still empty so it never overwrites typing
    setContent((c) => (c === '' ? initialMessage : c));
  }, [initialMessage, isLoadingPriorMessages, hasPriorMessages]);

  // Admin can use multi-channel; non-admins always use in_app
  // Hide selector entirely when no external channels are available (only in-app)
  const hasExternalChannels = !!recipientPhone || !!recipientEmail;
  const showChannelSelector =
    isSuperAdminFlag &&
    !isBalleeConversation &&
    !isSupportConversation &&
    hasExternalChannels;

  // Is the current user RESPONDING to a support conversation (super admin or
  // Ballee org member)? Responders write as the support identity, not the
  // requester.
  const isAdminSupportResponse = isSupportConversation && isSupportResponder;

  // While the support case is unescalated ('ai'), the Flow AI is the first
  // responder. The REQUESTER (not a responder) can hand off to a human, after
  // which the AI stops and the Ballee responders own the thread.
  const isAiSupport =
    isSupportConversation && supportStatus === 'ai' && !escalatedLocally;
  const showEscalate = isAiSupport && !isSupportResponder;

  // Trigger Flow AI response after sending a message
  const triggerBalleeResponse = useCallback(
    async (message: string) => {
      if (!isBalleeConversation) return;

      // Support conversations: Flow is the merged first responder — auto-reply
      // to every user message while the case is unescalated (no @Flow needed);
      // once a human takes over (status != 'ai') stop replying.
      if (isSupportConversation) {
        if (!isAiSupport) return;
      } else if (isGroupConversation) {
        // Non-support group: only respond when explicitly @Flow-mentioned.
        if (!FLOW_MENTION_PATTERN.test(message)) return;
      }

      // Strip the @Flow mention from the message sent to AI (group mentions only).
      const isGroupMention =
        !isSupportConversation &&
        isGroupConversation &&
        FLOW_MENTION_PATTERN.test(message);
      const cleanMessage = isGroupMention
        ? message.replace(FLOW_MENTION_PATTERN, '').trim()
        : message;

      if (!cleanMessage) return;

      setIsBalleeResponding(true);
      try {
        const response = await fetch('/api/chat/ballee', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            conversationId,
            message: cleanMessage,
            pageContext: pageContext ? JSON.stringify(pageContext) : undefined,
            isGroupMention: isGroupMention || undefined,
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to get Flow response');
        }

        // Response is now non-streaming, just read it
        await response.json();
      } catch (error) {
        console.error('Flow response error:', error);
        // Don't show toast - the message is still in the conversation
        // and Flow's response will appear via realtime when available
      } finally {
        setIsBalleeResponding(false);
      }
    },
    [
      conversationId,
      isBalleeConversation,
      isGroupConversation,
      isSupportConversation,
      isAiSupport,
      pageContext,
    ],
  );

  const { send, isLoading } = useSendMessage({
    conversationId,
    onSuccess: (_message) => {
      const sentContent = content.trim();
      setContent('');
      onClearReply?.();
      textareaRef.current?.focus();

      // Trigger Flow response after message is sent
      if (isBalleeConversation && sentContent) {
        triggerBalleeResponse(sentContent);
      }
    },
    onError: (error) => {
      toast.error(error.message || t('chat:composer.sendFailed'));
    },
  });

  const handleBookingInquirySubmit = useCallback(
    async (payload: BookingInquirySerialized) => {
      const body = formatBookingInquiryBody(payload);
      try {
        await send({
          content: body,
          metadata: {
            intent_type: 'booking',
            booking_payload: payload,
          },
          // Booking inquiries are always sent as plain in-app messages;
          // multi-channel delivery is opted into separately by admins
          // and shouldn't auto-engage on a structured inquiry.
          channel: undefined,
        });
        setIsBookingSheetOpen(false);
      } catch (error) {
        console.error('Booking inquiry send error:', error);
        toast.error(
          error instanceof Error
            ? error.message
            : t('chat:composer.bookingSendFailed'),
        );
      }
    },
    [send, t],
  );

  const handleSend = useCallback(async () => {
    const trimmedContent = content.trim();
    if (!trimmedContent || isLoading || isBalleeResponding || isSendingSupport)
      return;

    // For admin support responses, use the support action (sends as actual admin)
    if (isAdminSupportResponse) {
      setIsSendingSupport(true);
      try {
        await sendSupportMessageAction({
          conversationId,
          content: trimmedContent,
        });

        // Action throws on error, so if we get here it succeeded
        setContent('');
        onClearReply?.();
        textareaRef.current?.focus();
      } catch (error) {
        console.error('Support send error:', error);
        toast.error(t('chat:composer.sendFailed'));
      } finally {
        setIsSendingSupport(false);
      }
      return;
    }

    // Regular message send (with optional channel for admin multi-channel)
    send({
      content: trimmedContent,
      replyToId: replyTo?.id,
      channel: showChannelSelector ? selectedChannel : undefined,
    });
  }, [
    content,
    conversationId,
    isLoading,
    isBalleeResponding,
    isSendingSupport,
    onClearReply,
    replyTo,
    send,
    isAdminSupportResponse,
    selectedChannel,
    showChannelSelector,
    t,
  ]);

  const handleEscalate = useCallback(async () => {
    if (isEscalating) return;
    setIsEscalating(true);
    try {
      await escalateSupportAction({ conversationId });
      setEscalatedLocally(true);
    } catch (error) {
      console.error('Support escalation error:', error);
      toast.error(t('chat:composer.escalateFailed'));
    } finally {
      setIsEscalating(false);
    }
  }, [conversationId, isEscalating, t]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Send on Enter, newline on Shift+Enter
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleEmojiSelect = useCallback((emoji: string) => {
    setContent((prev) => prev + emoji);
    textareaRef.current?.focus();
  }, []);

  return (
    <div className="border-t p-4">
      {replyAsOrganizationName && (
        <p className="text-muted-foreground mb-2 flex items-center gap-1.5 text-xs">
          <Building2 className="h-3.5 w-3.5" aria-hidden />
          <Trans
            i18nKey="chat:org.replyingAs"
            values={{ organization: replyAsOrganizationName }}
          />
        </p>
      )}

      {/* Booking inquiry sheet (premium-dancer rail CTA). Auto-opens
          once on mount when ?intent=booking is set AND the conversation
          has no prior messages; otherwise toggled via the briefcase
          button in the input row. */}
      {showBookingButton && (
        <BookingInquiryForm
          open={isBookingSheetOpen}
          onOpenChange={setIsBookingSheetOpen}
          onSubmit={handleBookingInquirySubmit}
        />
      )}

      {/* Escalation: while Flow AI is the first responder, let the user hand
          off to a human. Disappears once escalated (status leaves 'ai'). */}
      {showEscalate && (
        <div className="mb-2 flex justify-center">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={handleEscalate}
            disabled={isEscalating}
          >
            <Headphones className="h-4 w-4" />
            <Trans i18nKey="chat:composer.talkToHuman" />
          </Button>
        </div>
      )}

      {/* Reply preview */}
      {replyTo && (
        <div className="bg-muted mb-2 flex items-center justify-between rounded-lg px-3 py-2">
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-xs font-medium">
              <Trans
                i18nKey="chat:composer.replyingTo"
                values={{ name: replyTo.senderName }}
              />
            </p>
            <p className="truncate text-sm">{replyTo.content}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearReply}
            className="h-6 px-2"
          >
            ✕
          </Button>
        </div>
      )}

      {/* Input row */}
      <div className="flex items-end gap-2">
        {/* Booking inquiry trigger — appears on direct conversations
            so users can pull up the structured form even when the
            inbox wasn't entered via ?intent=booking. */}
        {showBookingButton && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-10 w-10 flex-shrink-0"
            aria-label={t('chat:composer.bookingInquiry')}
            title={t('chat:composer.bookingInquiry')}
            onClick={() => setIsBookingSheetOpen(true)}
          >
            <Briefcase className="h-5 w-5" />
          </Button>
        )}

        {/* Channel selector (admin-only) */}
        {showChannelSelector && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-10 flex-shrink-0 gap-1 px-2"
              >
                {(() => {
                  const opt = CHANNEL_OPTIONS.find(
                    (o) => o.value === selectedChannel,
                  );
                  if (!opt) return null;
                  const Icon = opt.icon;
                  return <Icon className={`h-4 w-4 ${opt.color}`} />;
                })()}
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {CHANNEL_OPTIONS.filter((opt) => {
                if (opt.value === 'whatsapp') return !!recipientPhone;
                if (opt.value === 'email') return !!recipientEmail;
                return true;
              }).map((opt) => {
                const Icon = opt.icon;
                return (
                  <DropdownMenuItem
                    key={opt.value}
                    onClick={() => setSelectedChannel(opt.value)}
                    className="gap-2"
                  >
                    <Icon className={`h-4 w-4 ${opt.color}`} />
                    <span>{t(opt.labelKey)}</span>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Input pill — textarea with the emoji picker folded in on the right,
            matching Front/Rarible/Zendesk web chat widgets. */}
        <div className="border-input bg-background focus-within:ring-ring flex flex-1 items-end gap-1 rounded-lg border pr-1 transition-shadow focus-within:ring-1">
          <Textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label={t('chat:composer.messageInput')}
            placeholder={
              isAdminSupportResponse
                ? t('chat:composer.replyToPlaceholder')
                : t('chat:composer.placeholder')
            }
            className="max-h-[120px] min-h-[40px] resize-none border-0 bg-transparent px-3.5 py-2 shadow-none focus-visible:ring-0"
            rows={1}
            disabled={isLoading || isSendingSupport}
          />

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-foreground h-8 w-8 flex-shrink-0 rounded-full"
              >
                <Smile className="h-5 w-5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2" align="end">
              <div className="flex flex-wrap gap-1">
                {QUICK_EMOJIS.map((emoji) => (
                  <Button
                    key={emoji}
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-lg"
                    onClick={() => handleEmojiSelect(emoji)}
                  >
                    {emoji}
                  </Button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Send button */}
        <Button
          size="icon"
          className={
            isAdminSupportResponse
              ? 'h-10 w-10 flex-shrink-0 rounded-full bg-emerald-600 hover:bg-emerald-700'
              : 'h-10 w-10 flex-shrink-0 rounded-full'
          }
          onClick={handleSend}
          disabled={
            !content.trim() ||
            isLoading ||
            isBalleeResponding ||
            isSendingSupport
          }
        >
          <SendButtonIcon
            isBalleeResponding={isBalleeResponding}
            isSendingSupport={isSendingSupport}
            isAdminSupportResponse={isAdminSupportResponse ?? false}
          />
        </Button>
      </div>

      {/* Helper text */}
      <p className="text-muted-foreground mt-1 text-xs">
        {showChannelSelector && selectedChannel !== 'in_app' ? (
          <>
            <Trans i18nKey="chat:composer.sendingVia" />{' '}
            <span className="font-medium">
              {selectedChannel === 'whatsapp'
                ? t('chat:composer.channel.whatsapp')
                : t('chat:composer.channel.email')}
            </span>{' '}
            &middot; <Trans i18nKey="chat:composer.pressEnterShort" />
          </>
        ) : (
          <ComposerHelperText
            isSendingSupport={isSendingSupport}
            isAdminSupportResponse={isAdminSupportResponse ?? false}
            isBalleeResponding={isBalleeResponding}
          />
        )}
      </p>
    </div>
  );
}
